import { Fragment, ReactNode, memo } from 'react';

import { Flex } from '@backstage/ui';

import { ActivityItem } from '@spotify/plugin-confidence-activity-react';

import { DeltaFieldName } from '../DeltaFieldName';
import { DeltaText } from '../DeltaText';
import { Expression } from '../Expression';
import { ExpressionGroup } from '../ExpressionGroup';
import { FieldName } from '../FieldName/FieldName';
import {
  NormalizedExpressionDelta,
  NormalizedExpressionDeltaRenderer,
} from '../NormalizedExpressionDelta';
import { normalizeTargeting } from './normalizer';
import type { Rule } from './types';

// Constants
const MATCHING_TEXT = 'matching...';
// Proportion display component
const ProportionDisplay = memo(
  ({
    newRule,
    oldRule,
    disableExpression = false,
  }: {
    newRule?: Rule;
    oldRule?: Rule;
    disableExpression?: boolean;
  }) => {
    const newProportion =
      Number(newRule?.segment?.allocation?.proportion?.value ?? '0') * 100;

    const oldProportion =
      Number(oldRule?.segment?.allocation?.proportion?.value ?? '0') * 100;

    let proportionText: ReactNode = null;

    if (
      oldRule &&
      newRule &&
      oldProportion !== undefined &&
      newProportion !== undefined &&
      oldProportion !== newProportion
    ) {
      proportionText = (
        <Fragment>
          <Expression.Property>
            <DeltaText
              oldText={`${oldProportion?.toFixed(0)}%`}
              newText={`${newProportion?.toFixed(0)}%`}
              disableDiff
            />
          </Expression.Property>
        </Fragment>
      );
    } else {
      proportionText = (
        <Expression.Property>{newProportion?.toFixed(0)}%</Expression.Property>
      );
    }

    const newTargetingKeySelector =
      newRule?.targetingKeySelector || 'targeting_key';
    const oldTargetingKeySelector =
      oldRule?.targetingKeySelector || 'targeting_key';

    let targetingKeySelectorText: ReactNode = null;

    if (
      oldRule &&
      newRule &&
      oldTargetingKeySelector !== undefined &&
      newTargetingKeySelector !== undefined &&
      oldTargetingKeySelector !== newTargetingKeySelector
    ) {
      targetingKeySelectorText = (
        <Fragment>
          <DeltaFieldName field={oldTargetingKeySelector} change="removed" />
          <DeltaFieldName field={newTargetingKeySelector} change="added" />
        </Fragment>
      );
    } else {
      targetingKeySelectorText = (
        <FieldName field={newTargetingKeySelector || 'targeting_key'} />
      );
    }

    return (
      <ExpressionGroup>
        <Expression.Root>
          {proportionText}
          <Expression.Operator>of</Expression.Operator>
          {targetingKeySelectorText}

          {!disableExpression && (
            <Expression.Operator>{MATCHING_TEXT}</Expression.Operator>
          )}
        </Expression.Root>
      </ExpressionGroup>
    );
  },
);

ProportionDisplay.displayName = 'ProportionDisplay';

export const ToSection = ({
  oldRule,
  newRule,
}: {
  oldRule?: Rule;
  newRule?: Rule;
}) => {
  let delta;

  if (oldRule && newRule) {
    delta = NormalizedExpressionDelta.diffNormalizedExpression(
      normalizeTargeting(oldRule.segment?.targeting),
      normalizeTargeting(newRule.segment?.targeting),
    );
  } else {
    const normalizedExpression = normalizeTargeting(
      newRule?.segment?.targeting,
    );
    if (normalizedExpression) {
      delta =
        NormalizedExpressionDelta.fromNormalizedExpression(
          normalizedExpression,
        );
    }
  }

  const content = delta ? (
    <NormalizedExpressionDeltaRenderer expression={delta} />
  ) : null;

  return (
    <ActivityItem.Section label="To">
      <Flex
        direction="column"
        gap="1"
        align="start"
        justify="start"
        style={{ flexWrap: 'wrap' }}
      >
        <ProportionDisplay
          newRule={newRule}
          oldRule={oldRule}
          disableExpression={content === null}
        />

        {content}
      </Flex>
    </ActivityItem.Section>
  );
};
